import './styles';

import React from 'react';
import moment from 'moment';

import { constants, hockeyFormatter } from '@nhl/formatter-utilities';

import { TeamDisplayModule } from 'media-base-ui/views/components';
import gameStateTypes from 'media-base-ui/utils/gameStateTypes';
import gameTypes from 'media-base-ui/utils/gameTypes';

import GameInfo from './components/gameInfo';
import DocumentLink from '../documentLink';
import ReportLink from '../reportLink';
import { getSpecialEventName } from '../../../redux/ducks/games/selectors';
import { gameProps } from '../../../propTypes/gameList';

const sendGaClickEvent = label => {
    window.dataLayer.push({
        event: 'click',
        'event_category': 'Compact Game Component',
        'event_label': label
    });
};

const CompactGame = ({ game, displayGameCounts, displayReports = true }) => {

    const renderTeamDisplay = (team, score, className = '') => {

        const { season } = game;
        const gameState = game.state.id;
        const gameType =  game.gameType.id;
        const isOfficial = gameState === gameStateTypes.OFFICIAL || gameType === gameTypes.PRE_SEASON && gameState === gameStateTypes.HARD_FINAL;
        const isHomeTeam = game.homeTeam.id === team.id;
        const defeatedClass = isHomeTeam
            ? score < game.visitingScore && isOfficial ? 'defeated ' : ''
            : score < game.homeScore && isOfficial ? 'defeated ' : '';
        let record = '';

        if (gameType === gameTypes.REGULAR_SEASON || season.id === constants.SEASON_WITH_PLAYOFF_OTL) {
            let mostRecentTeamStats;
            if (isOfficial) {
                mostRecentTeamStats = game.teamByGameStats.find(teamStats => teamStats.team.id === team.id);
            }

            if (!isOfficial || !mostRecentTeamStats) {
                mostRecentTeamStats = team.stats.find(stats => stats.gameType.id === gameType);
            }

            if (!mostRecentTeamStats) {
                record = '';
            } else {
                const teamRecord = hockeyFormatter.getRecordForTeamStatsAndSeason(mostRecentTeamStats, season);

                record =
                    <div className={`record-container ${className}`}>
                        <div className="record">
                            {teamRecord}
                        </div>
                        {displayGameCounts
                            ? <div className="game-count">
                                {`Game ${isHomeTeam
                                    ? game.homeTeamGameNumber
                                    : game.visitingTeamGameNumber}
                                                ${isHomeTeam
                                    ? 'Home ' + game.homeTeamHomeGameNumber
                                    : 'Away ' + game.visitingTeamAwayGameNumber}`
                                }
                            </div>
                            : null
                        }
                    </div>;
            }
        }

        return  <TeamDisplayModule
                    team={team}
                    season={game.season.id}
                    teamDisplayProps={{
                        appendTeamName: record,
                        className: defeatedClass + className,
                    }}
                />
    };

    const renderSeriesInfo = (playoffTeamGames) => {
        const playoffTeamGame = playoffTeamGames[0];
        let leadingTeam = '';
        let leadDescription = '';
        let seriesScore = '';
        let isNotTied = false;

        if (playoffTeamGame.gamesNeededToWin > 0) {
            if (playoffTeamGame.seriesWins > playoffTeamGame.seriesLosses) {
                leadingTeam = ` - ${playoffTeamGame.team.triCode}`;
                seriesScore = ` ${playoffTeamGame.seriesWins} - ${playoffTeamGame.seriesLosses}`;
                isNotTied = true;
            } else if (playoffTeamGame.seriesWins < playoffTeamGame.seriesLosses) {
                leadingTeam = ` - ${playoffTeamGame.opponentTeam.triCode}`;
                seriesScore = ` ${playoffTeamGame.seriesLosses} - ${playoffTeamGame.seriesWins}`;
                isNotTied = true;
            // We do not show series score or lead descriptions for the Stanley Cup Qualifiers R.R.
            } else if (playoffTeamGame.seriesTitle !== 'Stanley Cup Qualifiers-Round Robin'){
                leadDescription = ' - Tied';
                seriesScore = ` ${playoffTeamGame.seriesWins} - ${playoffTeamGame.seriesLosses}`;
            }

            if (isNotTied) {
                if (playoffTeamGame.seriesDecidingGame) {
                    leadDescription = ' Wins'
                } else {
                    leadDescription = ' Leads'
                }
            }
        } else if (playoffTeamGame.seriesDecidingGame) {
            leadingTeam = ` - ${playoffTeamGame.seriesWinningTeam.triCode}`;
            seriesScore = ' Wins Total Goals';
        }

        return (
            <span>
                {playoffTeamGame.seriesTitle}{leadingTeam}{leadDescription}{seriesScore}
            </span>
        );
    };

    const liveGameClass = game.state.id > gameStateTypes.PREGAME && game.state.id < gameStateTypes.CRITICAL
        ? 'live'
        : game.state.id === gameStateTypes.CRITICAL ? 'critical' : '';
    const now = moment();
    const specialEventName = getSpecialEventName(game);

    return (
        <div className={`compact-game ${liveGameClass}`}>

            {game.gameType.id === gameTypes.PLAYOFFS && game.playoffTeamGames.length > 0 &&
                <div className="series-container">
                    {renderSeriesInfo(game.playoffTeamGames)}
                </div>
            }

            {
                specialEventName && <div className="special-event-container">{specialEventName}</div>
            }

            <div className="game-container">
                { renderTeamDisplay(game.visitingTeam, game.visitingScore) }
                <GameInfo game={game}/>
                { renderTeamDisplay(game.homeTeam, game.homeScore, 'align-right') }
            </div>

            {now.isSameOrAfter(moment(game.gameDate, constants.DATE_FORMAT), 'day') && displayReports
                ?   <ul className="downloads">
                        {game.notes &&
                            <DocumentLink
                                className="game-notes"
                                link={game.notes.reportUrl}
                                text={'Game Notes'}
                                onClick={sendGaClickEvent.bind(this, 'game notes')}
                                disabled={!game.notes.published}
                            />
                        }
                        {<ReportLink game={game} reportType={'RO'} reportName={'Roster Report'}
                                 disabled={!game.rosterFlag}/>}
                        {<ReportLink game={game} reportType={'GS'} reportName={'Game Summary'}
                                     disabled={!game.boxscoreFlag}/>}
                        {<ReportLink game={game} reportType={'ES'} reportName={'Event Summary'}
                                     disabled={!game.sshtFlag}/>}
                        {<ReportLink game={game} reportType={'PL'} reportName={'Full Play-by-Play'}
                                     disabled={!game.playByPlayFlag}/>}
                        {game.shootoutFlag &&
                        <ReportLink game={game} reportType={'SO'} reportName={'Shootout Summary'}/>}
                    </ul>
                :   null
            }
        </div>
    );
};

CompactGame.propTypes = gameProps;

export default CompactGame;
